import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import {
    FullLayout,
    FullLayoutProps,
    SnackbarCloseButton,
    useAvatarUrlDispatch,
} from '@europrocurement/flexy-components';

import { UseKeycloakCheckRole, UseKeycloakService } from '@europrocurement/l2d-keycloak';
/** end layouts */

/** pages */
import { Box } from '@mui/material';
import { SnackbarAction, SnackbarKey, SnackbarProvider } from 'notistack';
import {
    B2DLogoIcon,
    B2DSymboleIcon,
    L2DFLogoSmallIcon,
    plusIcon,
} from '@europrocurement/l2d-icons';

/** end pages */

/** menu */

/** end menu */

import { FullLayoutProvider } from '@europrocurement/flexy-components/components/templates/FullLayout/FullLayoutContext';
import { TiersAppDispatch, useUtilisateurService } from '@europrocurement/l2d-domain';
import { sha256 } from '@europrocurement/l2d-utils';
import {
    ManageAvatar,
    useMessagerieNotification,
    parcoursFormalitePathResolver,
} from '@europrocurement/l2d-modules';
import { useDispatch } from 'react-redux';
import { IconBadgeListItem } from '@europrocurement/flexy-components/components/molecules/IconBadgeList';
import { useResetRedux } from '../pages/Achats/components/forms/functions/generalFunctions';
import { useUserPreferencesBack } from '../hooks/useUserPreferencesBack';
import generateB2DPath, { GenerateB2DPathResponseType } from '../utils/generateB2DPath';
import B2DCustomHeader from './B2DCustomHeader';

const B2DLayout: React.FunctionComponent<Omit<FullLayoutProps, 'logo' | 'logoFolded'>> = function (
    props,
) {
    const ToCaptureRef = React.useRef<HTMLDivElement>(null); // ref

    const roleChecker = UseKeycloakCheckRole();
    const isInterne = roleChecker('realm:interne');

    const location = useLocation();
    const navigate = useNavigate();

    const kc = UseKeycloakService();

    const { avatarBaseUrl } = useUtilisateurService();

    const avatarUrlDispatch = useAvatarUrlDispatch();

    const [userIdentifier, setUserIdentifier] = React.useState<string | undefined>(undefined);

    React.useEffect(() => {
        const token = kc.getTokenParsed();
        if (token && token.preferred_username && token.preferred_username !== userIdentifier) {
            setUserIdentifier(token.preferred_username);
        }
    }, [kc, avatarBaseUrl, userIdentifier]);

    React.useEffect(() => {
        const updateAvatar = async (newIdentifier: string) => {
            const keycloakIdHash = await sha256(newIdentifier);
            // console.log("je vais mettre à jour l'avatar pour l'utilisateur", {
            //     type: 'update',
            //     payload: `${avatarBaseUrl + keycloakIdHash}?time=${new Date().getTime()}`,
            // });
            avatarUrlDispatch({
                type: 'update',
                payload: `${avatarBaseUrl + keycloakIdHash}?time=${new Date().getTime()}`,
            });
        };
        if (userIdentifier) {
            // console.log("j'ai un userIdentifier", userIdentifier);
            updateAvatar(userIdentifier);
        }
    }, [avatarBaseUrl, avatarUrlDispatch, userIdentifier]);

    useEffect(() => {
        if (!(roleChecker('realm:interne') || roleChecker('back-to-digital:oworker'))) {
            navigate('/errors/403');
        }
    }, [navigate, roleChecker]);

    const closeBtn: SnackbarAction = (key: SnackbarKey) => (
        <SnackbarCloseButton snackbarKey={key} />
    );

    const resetRedux = useResetRedux();

    const { customMenuItems, renderCurrentSelection } = useUserPreferencesBack();

    const [messageItemIconBadge, setMessageItemIconBadge] = useState<IconBadgeListItem[]>([]);
    const [notificationItemIconBadge, setNotificationItemIconBadge] = useState<IconBadgeListItem[]>(
        [],
    );
    const { messageItems, notificationItems } = useMessagerieNotification();

    useEffect(() => {
        if (messageItems && messageItems.length > 0) {
            setMessageItemIconBadge(messageItems);
        }
    }, [messageItems]);

    useEffect(() => {
        if (notificationItems && notificationItems.length > 0) {
            setNotificationItemIconBadge(notificationItems);
        }
    }, [notificationItems]);
    const dispatch = useDispatch<TiersAppDispatch>();

    const layoutProps: FullLayoutProps = {
        ...props,
        defaultContextActions: [],
        contextActionMainIcon: (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
            >
                <B2DSymboleIcon style={{ maxHeight: '24px' }} />
            </Box>
        ),
        logo: <B2DLogoIcon style={{ width: '100%', height: '100%' }} />,
        logoFolded: <L2DFLogoSmallIcon style={{ width: '100%', height: '100%' }} />,
        sidebarActions: [
            {
                icon: { ...plusIcon.props },
                label: 'Nouveau Dossier',
                onClick: () => {
                    dispatch({ type: 'prescripteur/deletemainSelected' });
                    navigate(parcoursFormalitePathResolver('stepPrescriber'));
                },
            },
        ],
    };

    const returnArrowClickCallbackHandle = useCallback(async () => {
        let response: GenerateB2DPathResponseType = {
            status: 'STAY',
        };
        let hasPerformedAction = false;

        resetRedux({});

        if (location.pathname.match(/\/fournisseurs\/rappels\/(\d+)/)) {
            navigate('fournisseurs/rappels');
            hasPerformedAction = true;
        }
        if (location.pathname.match(/\/achats\/liste\/saisir\/(\d+)/)) {
            response = isInterne
                ? generateB2DPath('listmediaobjectinternally')
                : generateB2DPath('listmediaobject');
        }
        if (
            location.pathname.match(/\/achats\/liste\/modifier/) ||
            location.pathname.match(/\/achats\/liste\/recap/)
        ) {
            response = generateB2DPath('listfacture');
        }
        if (location.pathname.match(/\/achats\/statement/)) {
            response = generateB2DPath('listInvoiceStatement');
        }

        if (response.status === 'OK') {
            navigate(response.path);
            hasPerformedAction = true;
        }

        return hasPerformedAction;
    }, [isInterne, location.pathname, resetRedux, navigate]);

    return (
        <SnackbarProvider
            maxSnack={3}
            autoHideDuration={3000}
            anchorOrigin={{
                horizontal: 'center',
                vertical: 'bottom',
            }}
            action={closeBtn}
            variant="success"
        >
            <div ref={ToCaptureRef}>
                <FullLayoutProvider>
                    <FullLayout
                        {...layoutProps}
                        headerProps={{
                            // returnArrowClickCallback: ()=>{navigateTo()} // quand mon callback est trigger, j'execute navigateTo()
                            handleClickExitButton: returnArrowClickCallbackHandle,
                            customHeader: <B2DCustomHeader />,
                            manageAvatar: <ManageAvatar />,
                            itemsMessage: messageItemIconBadge,
                            itemsNotification: notificationItemIconBadge,
                        }}
                        userPreferencesProps={{ customMenuItems, renderCurrentSelection }}
                    />
                </FullLayoutProvider>
            </div>
        </SnackbarProvider>
    );
};

export default B2DLayout;
